import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Seo } from "@/components/Seo";
import { Reveal, Tag } from "@/components/System";
import { SOURCES, SOURCE_CATEGORIES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sources = () => {
    const [query, setQuery] = useState("");
    const [cat, setCat] = useState("ALL");
    const [remote, setRemote] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/sources`)
            .then((r) => setRemote(r.data.sources))
            .catch(() => setRemote(null));
    }, []);

    const data = remote || SOURCES;

    const filtered = useMemo(
        () =>
            data.filter((s) => {
                const q = query.toLowerCase();
                const matchQ =
                    !q ||
                    [s.source, s.title, s.publisher, s.topic, s.claim].join(" ").toLowerCase().includes(q);
                const matchC = cat === "ALL" || s.topic === cat || (cat === "CIRCLE" && s.publisher.includes("Circle"));
                return matchQ && matchC;
            }),
        [data, query, cat]
    );

    return (
        <div>
            <Seo path="/sources" />
            <section className="mx-auto max-w-7xl px-4 pb-24 pt-36 sm:px-6">
                <div className="vm-micro mb-6">AGENTVM://SOURCES</div>
                <h1 className="text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                    VERIFY THE <span className="text-[#00D9FF] vm-glow">SOURCE.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                    Every external claim on this site resolves to an official source. This registry is the
                    verification layer for the website itself.
                </p>

                <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center">
                    <input
                        data-testid="sources-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="SEARCH SOURCE, TITLE, PUBLISHER, CLAIM"
                        aria-label="Search sources"
                        className="w-full border border-[rgba(0,217,255,0.2)] bg-[#080F18] px-4 py-2.5 font-mono text-[11px] tracking-[0.14em] text-[#F5F9FF] placeholder:text-[#B8C7D9]/40 focus:border-[#00D9FF]/60 focus:outline-none lg:max-w-md"
                    />
                    <div className="flex flex-wrap gap-2" data-testid="sources-categories">
                        {SOURCE_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid={`sources-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setCat(c)}
                                aria-pressed={cat === c}
                                className={`border px-3 py-1.5 font-mono text-[9px] tracking-[0.18em] transition-colors ${
                                    cat === c
                                        ? "border-[#00D9FF]/60 bg-[#00D9FF]/10 text-[#00D9FF]"
                                        : "border-[rgba(0,217,255,0.15)] text-[#B8C7D9]/60 hover:text-[#F5F9FF]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-8 overflow-x-auto border border-[rgba(0,217,255,0.12)]" data-testid="sources-table">
                    <table className="w-full min-w-[900px] border-collapse text-left">
                        <thead>
                            <tr className="border-b border-[rgba(0,217,255,0.15)] bg-[#080F18]">
                                {["SOURCE", "TITLE", "PUBLISHER", "DATE", "TOPIC", "WHAT IT VERIFIES", "LINK"].map((h) => (
                                    <th key={h} className="px-4 py-3 font-mono text-[9px] font-medium tracking-[0.22em] text-[#00D9FF]/70">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((s) => (
                                <tr key={s.id} className="border-b border-[rgba(0,217,255,0.07)] transition-colors hover:bg-[#00D9FF]/[0.03]">
                                    <td className="px-4 py-4 font-mono text-[11px] tracking-[0.1em] text-[#F5F9FF]">{s.source}</td>
                                    <td className="px-4 py-4 text-xs text-[#B8C7D9]">{s.title}</td>
                                    <td className="px-4 py-4 font-mono text-[10px] tracking-[0.1em] text-[#B8C7D9]/80">{s.publisher}</td>
                                    <td className="px-4 py-4 font-mono text-[10px] text-[#B8C7D9]/50">{s.date || "LIVE DOCUMENT"}</td>
                                    <td className="px-4 py-4">
                                        <Tag tone="beta">{s.topic}</Tag>
                                    </td>
                                    <td className="max-w-xs px-4 py-4 text-xs leading-relaxed text-[#B8C7D9]/85">{s.claim}</td>
                                    <td className="px-4 py-4">
                                        <a
                                            href={s.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`source-open-${s.id.toLowerCase()}`}
                                            className="whitespace-nowrap font-mono text-[10px] tracking-[0.18em] text-[#00D9FF] hover:underline"
                                        >
                                            OPEN SOURCE ↗
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="p-8 text-center font-mono text-xs tracking-[0.18em] text-[#B8C7D9]/60" data-testid="sources-empty">
                            NO SOURCES MATCH QUERY
                        </div>
                    )}
                </div>

                <Reveal delay={0.1}>
                    <div className="vm-panel-soft mt-8 flex flex-col gap-2 p-5 sm:flex-row sm:items-center sm:justify-between">
                        <span className="vm-micro flex items-center gap-2"><Tag tone="live">OFFICIAL SOURCE</Tag> REGISTRY POLICY</span>
                        <span className="vm-micro-dim">ONLY AUTHORITATIVE SOURCES. NO FABRICATED PROOF. {remote ? "SERVING FROM API." : "SERVING LOCAL REFERENCE COPY."}</span>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Sources;
